import { useState } from 'react'
import { message, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import brandService, { type CreateBrandData, type UpdateBrandData } from '../services/brand.service'
import type { Brand } from '../types/brand.types'

export const useBrandActions = () => {
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null)
    const [isDetailDrawerOpen, setIsDetailDrawerOpen] = useState(false)
    const [isFormModalOpen, setIsFormModalOpen] = useState(false)
    const [editingBrand, setEditingBrand] = useState<Brand | null>(null)
    const queryClient = useQueryClient()

    // Create brand mutation
    const createMutation = useMutation({
        mutationFn: (data: CreateBrandData) => brandService.createBrand(data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['brands'] })
            message.success('Brand created successfully!')
            setIsFormModalOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to create brand')
        },
    })

    // Update brand mutation
    const updateMutation = useMutation({
        mutationFn: ({ id, data }: { id: string; data: UpdateBrandData }) => 
            brandService.updateBrand(id, data),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['brands'] })
            message.success('Brand updated successfully!')
            setIsFormModalOpen(false)
            setIsDetailDrawerOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to update brand')
        },
    })

    // Delete brand mutation
    const deleteMutation = useMutation({
        mutationFn: (id: string) => brandService.deleteBrand(id),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: ['brands'] })
            message.success('Brand deleted successfully!')
            setIsDetailDrawerOpen(false)
        },
        onError: (error: any) => {
            message.error(error.response?.data?.message || 'Failed to delete brand')
        },
    })

    const handleViewDetails = (brand: Brand) => {
        setSelectedBrand(brand)
        setIsDetailDrawerOpen(true)
    }

    const handleAddBrand = () => {
        setEditingBrand(null)
        setIsFormModalOpen(true)
    }

    const handleEditBrand = (brand: Brand) => {
        setEditingBrand(brand)
        setIsFormModalOpen(true)
    }

    const handleDeleteBrand = (brand: Brand) => {
        Modal.confirm({
            title: 'Delete Brand',
            content: `Are you sure you want to delete ${brand.name}? This will affect ${brand.productCount} products.`,
            okText: 'Delete',
            okType: 'danger',
            onOk: () => {
                deleteMutation.mutate(brand.id)
            },
        })
    }

    const handleFormSubmit = (values: any) => {
        if (editingBrand) {
            // Update existing brand
            updateMutation.mutate({
                id: editingBrand.id,
                data: {
                    name: values.name,
                    description: values.description,
                    logo: values.logo,
                    status: values.status,
                },
            })
        } else {
            // Create new brand
            createMutation.mutate({
                name: values.name,
                description: values.description,
                logo: values.logo,
                status: values.status || 'active',
            })
        }
    }

    const closeDrawer = () => setIsDetailDrawerOpen(false)
    const closeFormModal = () => setIsFormModalOpen(false)

    return {
        selectedBrand,
        isDetailDrawerOpen,
        isFormModalOpen,
        editingBrand,
        handleViewDetails,
        handleAddBrand,
        handleEditBrand,
        handleDeleteBrand,
        handleFormSubmit,
        closeDrawer,
        closeFormModal,
        isSubmitting: createMutation.isPending || updateMutation.isPending,
    }
}
